import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '@/utils/logger';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Star, StarOff, 
  Loader2, Package2, FileText, MessageCircle, Check, X, AlertCircle,
  TrendingUp, Clock, Users, BarChart3, Download, CheckSquare, Square, Reply, Send, Pin, PinOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletePostId, setDeletePostId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const navigate = useNavigate();

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      logger.error('Failed to load analytics:', error);
    }
  }, []);

  const loadComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/comments`, { withCredentials: true });
      setComments(response.data || []);
    } catch (error) {
      logger.error('Failed to load comments:', error);
    }
  }, []);

  const checkAdminAndLoad = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(response.data || []);
      setIsAdmin(true);
      loadComments();
      loadAnalytics();
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load posts:', error);
        toast.error('Failed to load posts');
      }
    } finally {
      setLoading(false);
    }
  }, [loadComments, loadAnalytics]);

  useEffect(() => {
    checkAdminAndLoad();
  }, [checkAdminAndLoad]);

  const handleSelectPost = (postId) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const handleSelectAll = () => {
    const filteredPosts = posts.filter(post => 
      post.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (selectedPosts.length === filteredPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(filteredPosts.map(p => p.post_id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedPosts.length === 0) {
      toast.error('No posts selected');
      return;
    }

    const actionLabels = {
      publish: 'publish',
      unpublish: 'unpublish',
      delete: 'delete'
    };

    if (action === 'delete' && !window.confirm(`Are you sure you want to delete ${selectedPosts.length} posts? This cannot be undone.`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/admin/blog/bulk`, {
        post_ids: selectedPosts,
        action: action
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setSelectedPosts([]);
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Bulk action failed:', error);
      toast.error(error.response?.data?.detail || `Failed to ${actionLabels[action]} posts`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleExportSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscribers/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Subscribers exported successfully');
    } catch (error) {
      logger.error('Export failed:', error);
      toast.error('Failed to export subscribers');
    }
  };

  const handleDelete = async () => {
    if (!deletePostId) return;

    try {
      await axios.delete(`${API}/admin/blog/${deletePostId}`, { withCredentials: true });
      toast.success('Post deleted successfully');
      setDeletePostId(null);
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(
        `${API}/admin/blog/${post.post_id}`,
        { is_published: !post.is_published },
        { withCredentials: true }
      );
      toast.success(post.is_published ? 'Post unpublished' : 'Post published');
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to update post:', error);
      toast.error('Failed to update post');
    }
  };

  const toggleFeatured = async (post) => {
    try {
      await axios.put(
        `${API}/admin/blog/${post.post_id}`,
        { is_featured: !post.is_featured },
        { withCredentials: true }
      );
      toast.success(post.is_featured ? 'Removed from featured' : 'Added to featured');
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to update post:', error);
      toast.error('Failed to update post');
    }
  };

  const togglePinned = async (post) => {
    try {
      await axios.put(
        `${API}/admin/blog/${post.post_id}`,
        { is_pinned: !post.is_pinned },
        { withCredentials: true }
      );
      toast.success(post.is_pinned ? 'Post unpinned' : 'Post pinned to top');
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to update post:', error);
      toast.error('Failed to update post');
    }
  };

  const approveComment = async (commentId) => {
    try {
      await axios.put(`${API}/admin/blog/comments/${commentId}/approve`, {}, { withCredentials: true });
      toast.success('Comment approved');
      loadComments();
    } catch (error) {
      logger.error('Failed to approve comment:', error);
      toast.error('Failed to approve comment');
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/admin/blog/comments/${commentId}`, { withCredentials: true });
      toast.success('Comment deleted');
      loadComments();
    } catch (error) {
      logger.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(
        `${API}/admin/blog/comments/${commentId}/reply`,
        { reply: replyText },
        { withCredentials: true }
      );
      toast.success('Reply sent and comment approved');
      setReplyingTo(null);
      setReplyText('');
      loadComments();
    } catch (error) {
      logger.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const filteredPosts = posts.filter(p =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">
              You don&apos;t have permission to access the admin area.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-2">
              Blog Management
            </h1>
            <p className="text-muted-foreground">
              Create and manage your blog posts
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/blog/new')}
            className="rounded-full bg-primary text-primary-foreground shadow-lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Post
          </Button>
        </div>

        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="rounded-full bg-stone-100 p-1">
            <TabsTrigger value="analytics" className="rounded-full">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="posts" className="rounded-full">
              <FileText className="h-4 w-4 mr-2" />
              Posts ({posts.length})
            </TabsTrigger>
            <TabsTrigger value="comments" className="rounded-full">
              <MessageCircle className="h-4 w-4 mr-2" />
              Pending Comments ({comments.length})
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics ? (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-2xl bg-white border border-stone-100 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground">Total Posts</span>
                    </div>
                    <p className="text-3xl font-bold">{analytics.posts.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.posts.published} published, {analytics.posts.drafts} drafts
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white border border-stone-100 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Eye className="h-5 w-5 text-blue-600" />
                      </div>
                      <span className="text-sm text-muted-foreground">Total Views</span>
                    </div>
                    <p className="text-3xl font-bold">{analytics.views.total.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">across all posts</p>
                  </div>

                  <div className="rounded-2xl bg-white border border-stone-100 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-amber-600" />
                      </div>
                      <span className="text-sm text-muted-foreground">Scheduled</span>
                    </div>
                    <p className="text-3xl font-bold">{analytics.posts.scheduled}</p>
                    <p className="text-xs text-muted-foreground mt-1">posts queued</p>
                  </div>

                  <div className="rounded-2xl bg-white border border-stone-100 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-green-600" />
                      </div>
                      <span className="text-sm text-muted-foreground">Subscribers</span>
                    </div>
                    <p className="text-3xl font-bold">{analytics.subscribers.total}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleExportSubscribers}
                      className="text-xs text-green-600 hover:text-green-700 p-0 h-auto mt-1"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {/* Top Posts & Categories */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Posts */}
                  <div className="rounded-2xl bg-white border border-stone-100 p-6">
                    <h3 className="font-heading font-semibold mb-4 flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-primary" />
                      Top Posts by Views
                    </h3>
                    {analytics.top_posts.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_posts.map((post, idx) => (
                          <div 
                            key={post.post_id}
                            onClick={() => navigate(`/blog/${post.slug}`)}
                            className="flex items-center justify-between p-3 rounded-xl bg-stone-50 hover:bg-stone-100 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-muted-foreground">#{idx + 1}</span>
                              <div>
                                <p className="font-medium line-clamp-1">{post.title}</p>
                                <p className="text-xs text-muted-foreground capitalize">{post.category}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{post.view_count}</p>
                              <p className="text-xs text-muted-foreground">views</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No published posts yet</p>
                    )}
                  </div>

                  {/* Categories */}
                  <div className="rounded-2xl bg-white border border-stone-100 p-6">
                    <h3 className="font-heading font-semibold mb-4 flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2 text-primary" />
                      Posts by Category
                    </h3>
                    {analytics.categories.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.categories.map(cat => (
                          <div key={cat.category} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium capitalize">
                                {cat.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span>{cat.count} posts</span>
                              <span className="text-muted-foreground">{cat.views} views</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No posts yet</p>
                    )}
                  </div>
                </div>

                {/* Pending Comments Alert */}
                {analytics.comments.pending > 0 && (
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-amber-800">
                        {analytics.comments.pending} comments awaiting approval
                      </p>
                      <p className="text-sm text-amber-600">Review them in the Comments tab</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.querySelector('[value="comments"]')?.click()}
                      className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      Review
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </TabsContent>

          <TabsContent value="posts">
            {/* Search and Bulk Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              
              {selectedPosts.length > 0 && (
                <div className="flex items-center gap-2 bg-primary/5 px-4 py-2 rounded-xl">
                  <span className="text-sm font-medium">{selectedPosts.length} selected</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkAction('publish')}
                    disabled={bulkActionLoading}
                    className="rounded-full h-8"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Publish
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkAction('unpublish')}
                    disabled={bulkActionLoading}
                    className="rounded-full h-8"
                  >
                    <EyeOff className="h-3 w-3 mr-1" />
                    Unpublish
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleBulkAction('delete')}
                    disabled={bulkActionLoading}
                    className="rounded-full h-8"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedPosts([])}
                    className="rounded-full h-8"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Posts Table */}
            {filteredPosts.length === 0 ? (
              <div className="text-center py-16 rounded-3xl bg-white border border-stone-100">
                <FileText className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2">No posts yet</h3>
                <p className="text-muted-foreground mb-6">
                  Create your first blog post to get started.
                </p>
                <Button
                  onClick={() => navigate('/admin/blog/new')}
                  className="rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Post
                </Button>
              </div>
            ) : (
              <div className="rounded-2xl bg-white border border-stone-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-4 py-4 w-12">
                          <button
                            onClick={handleSelectAll}
                            className="p-1 hover:bg-stone-200 rounded transition-colors"
                          >
                            {selectedPosts.length === filteredPosts.length && filteredPosts.length > 0 ? (
                              <CheckSquare className="h-5 w-5 text-primary" />
                            ) : (
                              <Square className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                        </th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Post</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Category</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Views</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {filteredPosts.map((post) => (
                        <tr key={post.post_id} className={`hover:bg-stone-50 transition-colors ${selectedPosts.includes(post.post_id) ? 'bg-primary/5' : ''}`}>
                          <td className="px-4 py-4">
                            <button
                              onClick={() => handleSelectPost(post.post_id)}
                              className="p-1 hover:bg-stone-200 rounded transition-colors"
                            >
                              {selectedPosts.includes(post.post_id) ? (
                                <CheckSquare className="h-5 w-5 text-primary" />
                              ) : (
                                <Square className="h-5 w-5 text-muted-foreground" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium">{post.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">{post.excerpt}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="capitalize text-sm">{post.category}</span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {formatDate(post.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                post.is_published 
                                  ? 'bg-green-100 text-green-700' 
                                  : post.scheduled_at 
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-stone-100 text-stone-600'
                              }`}>
                                {post.is_published ? 'Published' : post.scheduled_at ? 'Scheduled' : 'Draft'}
                              </span>
                              {post.is_pinned && (
                                <Pin className="h-4 w-4 text-amber-500" />
                              )}
                              {post.is_featured && (
                                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                              )}
                              {post.scheduled_at && !post.is_published && (
                                <span className="text-xs text-muted-foreground">
                                  {new Date(post.scheduled_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {post.view_count}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => togglePublished(post)}
                                title={post.is_published ? 'Unpublish' : 'Publish'}
                                className="h-8 w-8 p-0"
                              >
                                {post.is_published ? (
                                  <EyeOff className="h-4 w-4 text-stone-500" />
                                ) : (
                                  <Eye className="h-4 w-4 text-stone-500" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleFeatured(post)}
                                title={post.is_featured ? 'Remove from featured' : 'Add to featured'}
                                className="h-8 w-8 p-0"
                              >
                                {post.is_featured ? (
                                  <StarOff className="h-4 w-4 text-yellow-500" />
                                ) : (
                                  <Star className="h-4 w-4 text-stone-500" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => togglePinned(post)}
                                title={post.is_pinned ? 'Unpin' : 'Pin to top'}
                                className="h-8 w-8 p-0"
                              >
                                {post.is_pinned ? (
                                  <PinOff className="h-4 w-4 text-amber-500" />
                                ) : (
                                  <Pin className="h-4 w-4 text-stone-500" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/admin/blog/${post.post_id}/edit`)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="h-4 w-4 text-stone-500" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeletePostId(post.post_id)}
                                className="h-8 w-8 p-0 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 text-stone-500 hover:text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="comments">
            {comments.length === 0 ? (
              <div className="text-center py-16 rounded-3xl bg-white border border-stone-100">
                <MessageCircle className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2">No pending comments</h3>
                <p className="text-muted-foreground">
                  New comments will appear here for approval.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {comments.map(comment => (
                  <div key={comment.comment_id} className="rounded-2xl bg-white border border-stone-100 p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium">{comment.author_name}</p>
                        <p className="text-sm text-muted-foreground">{comment.author_email}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          onClick={() => approveComment(comment.comment_id)}
                          className="rounded-full bg-green-600 hover:bg-green-700"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReplyingTo(replyingTo === comment.comment_id ? null : comment.comment_id);
                            setReplyText('');
                          }}
                          className="rounded-full"
                        >
                          <Reply className="h-4 w-4 mr-1" />
                          Reply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteComment(comment.comment_id)}
                          className="rounded-full text-destructive hover:bg-red-50"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <p className="text-stone-600">{comment.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      On post: {posts.find(p => p.post_id === comment.post_id)?.title || comment.post_id}
                    </p>
                    
                    {/* Reply Form */}
                    {replyingTo === comment.comment_id && (
                      <div className="mt-4 pt-4 border-t border-stone-100">
                        <div className="flex gap-3">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write your reply..."
                            className="flex-1 min-h-[80px] p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setReplyingTo(null)}
                            className="rounded-full"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleReply(comment.comment_id)}
                            disabled={sendingReply || !replyText.trim()}
                            className="rounded-full"
                          >
                            {sendingReply ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Send className="h-4 w-4 mr-1" />
                            )}
                            Send Reply
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Existing Reply */}
                    {comment.admin_reply && (
                      <div className="mt-4 pt-4 border-t border-stone-100 bg-primary/5 -mx-6 -mb-6 px-6 pb-6 rounded-b-2xl">
                        <p className="text-xs text-primary font-medium mb-2">Your Reply:</p>
                        <p className="text-stone-600">{comment.admin_reply}</p>
                        {comment.replied_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Replied on {formatDate(comment.replied_at)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletePostId} onOpenChange={() => setDeletePostId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
              All comments on this post will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
